import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, checkUsername } from '../services/api';
import coupleCheckLogo from '../assets/couple-check-logo.png';
import './Signup.css';

function Signup() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    malePin: '',
    femalePin: '',
    securityQuestion: '',
    securityAnswer: ''
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'malePin' || name === 'femalePin') {
      if (value.length <= 4 && /^\d*$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!formData.username || !formData.password) {
        setError('Username and password are required');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      if (formData.malePin && formData.malePin.length !== 4) {
        setError('Male PIN must be exactly 4 digits');
        setLoading(false);
        return;
      }

      if (formData.femalePin && formData.femalePin.length !== 4) {
        setError('Female PIN must be exactly 4 digits');
        setLoading(false);
        return;
      }

      if (!formData.securityQuestion || formData.securityQuestion.trim().length < 5) {
        setError('Please enter a security question (min 5 characters)');
        setLoading(false);
        return;
      }

      if (!formData.securityAnswer || formData.securityAnswer.trim().length < 2) {
        setError('Please provide an answer to your security question (min 2 characters)');
        setLoading(false);
        return;
      }

      const usernameCheck = await checkUsername(formData.username);
      if (usernameCheck.exists) {
        setError('Username already exists');
        setLoading(false);
        return;
      }

      const response = await registerUser({
        username: formData.username,
        password: formData.password,
        malePin: formData.malePin,
        femalePin: formData.femalePin,
        securityQuestion: formData.securityQuestion.trim(),
        securityAnswer: formData.securityAnswer
      });

      if (response.success) {
        setSuccess('Registration successful! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(response.message || 'Registration failed');
      }

    } catch (err) {
      setError('An error occurred during registration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
         <div className="login-logo">
          <img src={coupleCheckLogo} alt="Couple Check Logo" />
        </div>
        <h2>Welcome!</h2>
        <h2>Create Your Couple Check Account</h2>
        <p className="signup-subtitle">Start journaling your relationship journey</p>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">Username <span className="required">*</span></label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              required
              autoComplete="username"
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Password <span className="required">*</span></label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password (min 6 characters)"
              required
              autoComplete="new-password"
            />
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password <span className="required">*</span></label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              required
              autoComplete="new-password"
            />
          </div>

          {/* Security Question Section */}
          <div className="section-divider">
            <div className="divider-line"></div>
            <span className="divider-text">Account Recovery <span className="required">*</span></span>
            <div className="divider-line"></div>
          </div>

          <div className="pin-section-description security-note">
            <p>🔑 Set a security question so you can reset your password if you forget it</p>
          </div>

          {/* ✅ UPDATED: Free-text input instead of dropdown */}
          <div className="form-group">
            <label htmlFor="securityQuestion">
              Security Question <span className="required">*</span>
            </label>
            <input
              type="text"
              id="securityQuestion"
              name="securityQuestion"
              value={formData.securityQuestion}
              onChange={handleChange}
              placeholder="e.g. What was my first pet's name?"
              maxLength="200"
              autoComplete="off"
            />
            <small className="pin-hint">Write any question only you know the answer to</small>
          </div>

          <div className="form-group">
            <label htmlFor="securityAnswer">
              Your Answer <span className="required">*</span>
            </label>
            <input
              type="text"
              id="securityAnswer"
              name="securityAnswer"
              value={formData.securityAnswer}
              onChange={handleChange}
              placeholder="Enter your answer (not case-sensitive)"
              autoComplete="off"
            />
            <small className="pin-hint">Remember this — you'll need it to reset your password</small>
          </div>

          {/* PIN Section */}
          <div className="pin-section-divider section-divider">
            <div className="divider-line"></div>
            <span className="divider-text">Privacy PINs (Optional)</span>
            <div className="divider-line"></div>
          </div>

          <div className="pin-section-description">
            <p>🔒 Set separate PINs for each partner to protect private entries</p>
          </div>

          {/* Male Partner PIN */}
          <div className="form-group">
            <label htmlFor="malePin">
              ♂️ Male Partner PIN
              <span className="field-hint">For male partner's private entries</span>
            </label>
            <input
              type="password"
              id="malePin"
              name="malePin"
              value={formData.malePin}
              onChange={handleChange}
              placeholder="4-digit PIN (optional)"
              maxLength="4"
              className="pin-input"
              inputMode="numeric"
            />
            <small className="pin-info">
              {formData.malePin.length > 0 ? (
                <span className="pin-progress">
                  {formData.malePin.length}/4 digits
                  {formData.malePin.length === 4 && <span className="pin-complete"> ✓</span>}
                </span>
              ) : (
                <span className="pin-hint">Enter 4-digit PIN or leave blank</span>
              )}
            </small>
          </div>

          {/* Female Partner PIN */}
          <div className="form-group">
            <label htmlFor="femalePin">
              ♀️ Female Partner PIN
              <span className="field-hint">For female partner's private entries</span>
            </label>
            <input
              type="password"
              id="femalePin"
              name="femalePin"
              value={formData.femalePin}
              onChange={handleChange}
              placeholder="4-digit PIN (optional)"
              maxLength="4"
              className="pin-input"
              inputMode="numeric"
            />
            <small className="pin-info">
              {formData.femalePin.length > 0 ? (
                <span className="pin-progress">
                  {formData.femalePin.length}/4 digits
                  {formData.femalePin.length === 4 && <span className="pin-complete"> ✓</span>}
                </span>
              ) : (
                <span className="pin-hint">Enter 4-digit PIN or leave blank</span>
              )}
            </small>
          </div>

          

          <button type="submit" className="signup-button" disabled={loading}>
            {loading ? (
              <><span className="spinner"></span>Creating Account...</>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <p className="login-link">
          Already have an account? <a href="/login">Login here</a>
        </p>
      </div>
    </div>
  );
}

export default Signup;